// ────────────────────────────────────────────────────────────────
// core/auth/auth.service.ts — the only place that talks to /api/auth/*
// (§4.1). Owns login, the MFA step, refresh rotation (shared in-flight),
// logout, TOTP enrollment, and the silent-refresh timer (§6.1: refresh
// at 80% of the access TTL). Writes results into SessionStore.
// ────────────────────────────────────────────────────────────────

import { DestroyRef, Injectable, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { api } from '../http/api';
import { SessionStore } from './session.store';
import {
  LoginResponse,
  MfaMethod,
  TokenPairResponse,
  TotpEnrollResponse,
} from '../models/admin.model';

// Refresh this far before the access token actually expires (80% of TTL).
const REFRESH_AT_FRACTION = 0.8;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // Shared in-flight refresh so a burst of 401s (or the silent timer firing
  // alongside a request) triggers exactly one /refresh round-trip.
  private refreshInFlight: Observable<TokenPairResponse> | null = null;
  private silentTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // (Re)schedule the silent refresh whenever the session (hence accessExp)
    // changes. Cleared on sign-out.
    effect(() => {
      const session = this.session.session();
      this.clearSilentTimer();
      if (!session || !session.accessExp) return;
      const ttl = session.accessExp - Date.now();
      const delay = Math.max(0, ttl * REFRESH_AT_FRACTION);
      this.silentTimer = setTimeout(() => {
        this.refresh().subscribe({ error: () => {} });
      }, delay);
    });

    this.destroyRef.onDestroy(() => this.clearSilentTimer());
  }

  // ── login → either a finished session or the MFA challenge ──
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(api('auth/login'), { email, password }).pipe(
      tap((res) => {
        if (res.step === 'done') this.session.setSession(res);
      })
    );
  }

  // ── MFA step (§4.1) ──
  verifyMfa(mfaToken: string, method: MfaMethod, code: string): Observable<TokenPairResponse> {
    return this.http
      .post<TokenPairResponse>(api('auth/mfa/verify'), { mfaToken, method, code })
      .pipe(tap((pair) => this.session.setSession(pair)));
  }

  sendMfaEmailCode(mfaToken: string): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(api('auth/mfa/email-code'), { mfaToken });
  }

  // ── refresh rotation (shared) ──
  refresh(): Observable<TokenPairResponse> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const refreshToken = this.session.refreshToken();
    if (!refreshToken) {
      this.forceLogout();
      return of<TokenPairResponse>(null as unknown as TokenPairResponse);
    }

    this.refreshInFlight = this.http
      .post<TokenPairResponse>(api('auth/refresh'), { refreshToken })
      .pipe(
        tap((pair) => this.session.updateTokens(pair.accessToken, pair.refreshToken)),
        shareReplay(1),
        finalize(() => (this.refreshInFlight = null))
      );
    return this.refreshInFlight;
  }

  // ── logout (best-effort server revoke, always clears locally) ──
  logout(): void {
    const refreshToken = this.session.refreshToken();
    if (refreshToken) {
      this.http.post(api('auth/logout'), { refreshToken }).subscribe({
        next: () => {},
        error: () => {},
      });
    }
    this.forceLogout();
  }

  // Clear session + return to sign-in without any network call.
  forceLogout(): void {
    this.clearSilentTimer();
    this.session.clear();
    void this.router.navigate(['/sign-in']);
  }

  // ── TOTP enrollment (authenticated) ──
  enrollTotp(): Observable<TotpEnrollResponse> {
    return this.http.post<TotpEnrollResponse>(api('auth/totp/enroll'), {});
  }

  verifyTotp(code: string): Observable<{ enabled: boolean }> {
    return this.http.post<{ enabled: boolean }>(api('auth/totp/verify'), { code });
  }

  private clearSilentTimer(): void {
    if (this.silentTimer) {
      clearTimeout(this.silentTimer);
      this.silentTimer = null;
    }
  }
}
